import { Reducer, AnyAction } from "redux"
import _ from "lodash"
import {
  LiveMeetingActionTypes,
  CaseStage,
  LiveMeetingCableActionTypes,
  ILiveMeetingConfigState,
  IConsent,
} from "./../liveMeetingTypes"
import { SharedActionTypes } from "./../../../../shared/state/sharedTypes"
import * as UUID from "../../../../shared/helpers/uuidHelpers"

export const initialState: ILiveMeetingConfigState = {
  timestamp: 0,
  version: 0,
  userConfigs: {},
  selectedMeetingCaseId: null,
  sharedViewHoveredAssetId: null,
  sharedViewSelectedAssetId: null,
  sharedViewSelectedAssetTypeFilters: null,
  sharedPdfState: { rotation: 0 },
  sharedImageZoomState: {
    zoom: 1,
    posX: 0,
    posY: 0
  },
  coordinatorId: null,
  presenterId: null,
  scribeId: null,
  recommendations: {},
  caseStage: CaseStage.PRESENTING,
  consents: {},
  speaking: {},
}

export const liveMeetingConfigReducer: Reducer<ILiveMeetingConfigState> = (state: ILiveMeetingConfigState = initialState, action: AnyAction): ILiveMeetingConfigState => {
  let liveMeetingConfig: ILiveMeetingConfigState

  switch (action.type) {
    case SharedActionTypes.REHYDRATE:
      if (!action.payload?.liveMeetingConfig) return state
      if (action.payload.liveMeetingConfig.consents == undefined) {
        action.payload.liveMeetingConfig.consents = {}
      }
      return {
        ...state,
        ...action.payload.liveMeetingConfig
      }

    case LiveMeetingActionTypes.FETCH_LIVE_MEETING_CONFIG_ASYNC_SUCCESS: {
      liveMeetingConfig = addUserMeetingRoleProperties(action.payload)
      // these lines below is needed, as the recommendations object is transformed
      // to camelcase by default (because of the axios pipeline we set up).
      // so the line below transforms the keys back to standard uuid format.
      // The same applies to the consents
      liveMeetingConfig.recommendations = _.transform(liveMeetingConfig.recommendations, (result, v, k) => {
        result[UUID.addHyphen(k).toLowerCase()] = v
      }) as { [caseId: string]: string }
      liveMeetingConfig.consents = _.transform(liveMeetingConfig.consents, (result, v, k) => {
        result[UUID.addHyphen(k).toLowerCase()] = v
      }) as { [caseId: string]: IConsent }
      return {
        ...state,
        ...liveMeetingConfig
      }
    }

    case LiveMeetingCableActionTypes.SELECT_MEETING_CASE:
      return {
        ...state,
        selectedMeetingCaseId: action.payload,
        caseStage: CaseStage.PRESENTING
      }

    case LiveMeetingCableActionTypes.UPDATE_SHARED_VIEW_SELECTED_ASSET:
      return {
        ...state,
        sharedViewSelectedAssetId: action.payload.assetId
      }

    case LiveMeetingCableActionTypes.UPDATE_SHARED_IMAGE_ZOOM_STATE:
      return {
        ...state,
        sharedImageZoomState: action.payload
      }

    case LiveMeetingCableActionTypes.TOGGLE_IS_ONLINE:
      return updateUserConfig(state, action.payload, "isOnline")

    case LiveMeetingCableActionTypes.TOGGLE_IS_SHARING_AUDIO:
      return updateUserConfig(state, action.payload, "isSharingAudio")

    case LiveMeetingCableActionTypes.TOGGLE_IS_SHARING_VIDEO:
      return updateUserConfig(state, action.payload, "isSharingVideo")

    case LiveMeetingCableActionTypes.TOGGLE_IS_SHARING_SCREEN:
      return updateUserConfig(state, action.payload, "isSharingScreen")

    case LiveMeetingActionTypes.USER_SPEAKING: {
      const speakingNow = action.payload.speakingNow
      const speakerId = action.payload.userId

      return {
        ...state,
        speaking: {
          ...state.speaking,
          [speakerId]: speakingNow
        }
      }
    }

    case LiveMeetingCableActionTypes.UPDATE_CONFIG: {
      liveMeetingConfig = action.payload

      if (liveMeetingConfig.version < state.version) { return state }

      liveMeetingConfig.userConfigs = _.mapKeys(
        liveMeetingConfig.userConfigs,
        "id"
      )

      liveMeetingConfig = addUserMeetingRoleProperties(liveMeetingConfig)
      if (liveMeetingConfig.consents == undefined) {
        liveMeetingConfig.consents = {}
      }
      return {
        ...state,
        ...liveMeetingConfig
      }
    }

    default:
      return state
  }
}

const updateUserConfig = (state: ILiveMeetingConfigState, payload: { userId: string }, property: string) => {
  return {
    ...state,
    userConfigs: {
      ...state.userConfigs,
      [payload.userId]: {
        ...state.userConfigs[payload.userId],
        [property]: payload[property],
      },
    },
  }
}

const addUserMeetingRoleProperties = (liveMeetingConfig: ILiveMeetingConfigState) => {
  const userConfigs = liveMeetingConfig.userConfigs
  let userId: keyof typeof userConfigs
  for (userId in userConfigs) {
    userConfigs[userId].isCoordinator = userId === liveMeetingConfig.coordinatorId
    userConfigs[userId].isPresenter = userId === liveMeetingConfig.presenterId
    userConfigs[userId].isScribe = userId === liveMeetingConfig.scribeId
  }
  return liveMeetingConfig
}
